import { isBlockPart, blockRoots } from "../native/blocks";
import { setNativeIcon } from "./native-icons";
import type { NativeSession } from "../native/session";
import {
  assignGroup,
  groupMembers,
  ungroup,
  clippingBase,
} from "../native/groups";
import { selectable } from "../native/geometry";
import type { SceneNode } from "../scene/model";
export class NativeLayerGroups {
  private lastNodes: readonly SceneNode[] | null = null;
  private members = new Map<string, SceneNode[]>();
  private rows = new Map<string, HTMLElement>();
  private menu: HTMLElement | null = null;
  private renaming: string | null = null;
  private list = document.getElementById("native-layers")!;
  constructor(
    private session: NativeSession,
    private changed: () => void,
    private blocked: () => boolean,
    private select: () => void,
  ) {
    this.list.addEventListener("contextmenu", (e) => {
      if (this.blocked()) return;
      const row = (e.target as HTMLElement).closest<HTMLElement>(
        "[data-layer-id],[data-group-id]",
      );
      if (!row) return;
      e.preventDefault();
      const n = session.page.nodes.find((n) => n.id === row.dataset.layerId);
      if (n && !session.selectedIds.includes(n.id)) session.select([n.id]);
      this.context(e.clientX, e.clientY, n, row.dataset.groupId);
      this.changed();
    });
    document.addEventListener(
      "pointerdown",
      (e) => {
        if (this.menu && !this.menu.contains(e.target as Node)) this.close();
      },
      true,
    );
    window.addEventListener(
      "keydown",
      (e) => {
        if (
          (e.target as HTMLElement).closest(
            "input,textarea,select,[contenteditable=true]",
          ) ||
          this.blocked()
        )
          return;
        if (e.key === "Escape" && this.menu) {
          e.preventDefault();
          e.stopImmediatePropagation();
          this.close();
        }
        if ((e.ctrlKey || e.metaKey) && e.code === "KeyG") {
          e.preventDefault();
          e.stopImmediatePropagation();
          if (e.shiftKey) {
            for (const id of new Set(
              session.selectedNodes
                .map((n) => n.layer.groupId)
                .filter((id): id is string => typeof id === "string"),
            ))
              session.replaceNodes(ungroup(session.page.nodes, id));
            session.finish();
          } else this.group();
          this.changed();
        }
      },
      true,
    );
  }
  get interacting() {
    return !!this.renaming;
  }
  cancel() {
    this.close();
    this.changed();
  }
  private close() {
    this.menu?.remove();
    this.menu = null;
  }
  private group() {
    if (
      this.session.selectedNodes.length < 2 ||
      this.session.selectedNodes.some(isBlockPart)
    )
      return;
    const id = crypto.randomUUID(),
      name =
        "Группа " +
        (new Set(
          this.session.page.nodes.map((n) => n.layer.groupId).filter(Boolean),
        ).size +
          1);
    this.session.replaceNodes(
      assignGroup(this.session.page.nodes, this.session.selectedIds, id, name),
    );
    this.session.finish();
    this.changed();
  }
  private groupPatch(id: string, patch: Record<string, unknown>) {
    this.session.replaceMany(
      groupMembers(this.session.page.nodes, id).map((n) => ({
        ...n,
        layer: { ...n.layer, ...patch },
      })),
    );
    this.session.finish();
    this.changed();
  }
  private rename(id: string, row: HTMLElement) {
    if (this.renaming) return;
    this.renaming = id;
    const label = row.querySelector<HTMLElement>(".layer-label")!,
      input = document.createElement("input"),
      name = String(
        groupMembers(this.session.page.nodes, id)[0]?.layer.groupName ??
          "Группа",
      );
    input.value = name;
    input.className = "layer-name";
    label.hidden = true;
    label.after(input);
    let done = false;
    const finish = (commit: boolean) => {
      if (done) return;
      done = true;
      this.renaming = null;
      input.remove();
      label.hidden = false;
      if (commit)
        this.groupPatch(id, { groupName: input.value.trim() || name });
      this.changed();
    };
    input.onblur = () => finish(true);
    input.onkeydown = (e) => {
      if (["Enter", "Escape"].includes(e.key)) {
        e.preventDefault();
        e.stopPropagation();
        finish(e.key === "Enter");
      }
    };
    input.focus();
    input.select();
  }
  private context(x: number, y: number, node?: SceneNode, group?: string) {
    this.close();
    const menu = document.createElement("div");
    menu.className = "layer-context-menu";
    menu.setAttribute("role", "menu");
    const add = (title: string, run: () => void, disabled = false) => {
      const b = document.createElement("button");
      b.textContent = title;
      b.disabled = disabled;
      b.onclick = () => {
        this.close();
        run();
        this.changed();
      };
      menu.append(b);
    };
    if (group) {
      const row = this.rows.get(group)!;
      add("Переименовать", () => this.rename(group, row));
      if (!blockRoots(groupMembers(this.session.page.nodes, group)).length)
        add(
          groupMembers(this.session.page.nodes, group)[0]?.layer.groupCollapsed
            ? "Раскрыть группу"
            : "Свернуть группу",
          () =>
            this.groupPatch(group, {
              groupCollapsed: !groupMembers(this.session.page.nodes, group)[0]
                ?.layer.groupCollapsed,
            }),
        );
      add("Разгруппировать", () => {
        this.session.replaceNodes(ungroup(this.session.page.nodes, group));
        this.session.finish();
      });
    } else {
      add(
        "Переименовать",
        () =>
          document
            .querySelector<HTMLElement>(
              `[data-layer-id="${node?.id}"] .native-layer-name`,
            )
            ?.dispatchEvent(new MouseEvent("dblclick", { bubbles: true })),
        !node || this.session.selectedNodes.length !== 1,
      );
      if (node?.kind === "image")
        add(
          node.layer.clipToBelow
            ? "Отменить обтравочную маску"
            : "Создать обтравочную маску",
          () => {
            this.session.replaceNode({
              ...node,
              layer: { ...node.layer, clipToBelow: !node.layer.clipToBelow },
            });
            this.session.finish();
          },
          !!node.layer.locked ||
            (!node.layer.clipToBelow &&
              !clippingBase(this.session.page.nodes, node)),
        );
      add(
        "Группировать слои",
        () => this.group(),
        this.session.selectedNodes.length < 2 ||
          this.session.selectedNodes.some(isBlockPart),
      );
    }
    document.getElementById("native-editor")!.append(menu);
    menu.style.left =
      Math.max(8, Math.min(x, innerWidth - menu.offsetWidth - 8)) + "px";
    menu.style.top =
      Math.max(8, Math.min(y, innerHeight - menu.offsetHeight - 8)) + "px";
    this.menu = menu;
  }
  render() {
    if (this.lastNodes === this.session.page.nodes) {
      const selected = new Set(this.session.selectedIds);
      for (const [id, members] of this.members) {
        const eligible = members.filter(selectable),
          count = eligible.filter((n) => selected.has(n.id)).length;
        const row = this.rows.get(id);
        if (row)
          row.dataset.groupSelection =
            count === 0
              ? "none"
              : count === eligible.length
                ? "all"
                : "partial";
      }
      return;
    }
    this.lastNodes = this.session.page.nodes;
    this.members.clear();
    for (const n of this.lastNodes)
      if (typeof n.layer.groupId === "string" && n.layer.groupId) {
        const group = this.members.get(n.layer.groupId) || [];
        group.push(n);
        this.members.set(n.layer.groupId, group);
      }
    const nodeRows = new Map(
      [...this.list.querySelectorAll<HTMLElement>("[data-layer-id]")].map(
        (r) => [r.dataset.layerId, r],
      ),
    );
    const clippingBases = new Set(
      this.lastNodes
        .filter((n) => n.layer.clipToBelow)
        .map((n) => clippingBase(this.lastNodes!, n)),
    );
    const nodes = this.session.page.nodes,
      groups = new Set(
        nodes
          .map((n) => n.layer.groupId)
          .filter((id): id is string => typeof id === "string" && !!id),
      );
    for (const [id, row] of this.rows)
      if (!groups.has(id)) {
        row.remove();
        this.rows.delete(id);
      }
    for (const node of nodes) {
      const row = nodeRows.get(node.id);
      if (!row) continue;
      const grouped =
        typeof node.layer.groupId === "string" && !!node.layer.groupId;
      row.classList.toggle("layer-group-child", grouped);
      row.hidden =
        grouped && (node.layer.groupCollapsed === true || isBlockPart(node));
      row.classList.toggle("is-clipped", node.layer.clipToBelow === true);
      let indicator = row.querySelector<HTMLElement>(
        ".layer-clipping-indicator",
      );
      if (node.layer.clipToBelow && !indicator) {
        indicator = document.createElement("span");
        indicator.className = "layer-clipping-indicator";
        indicator.innerHTML =
          '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 4H8v16m-5-5 5 5 5-5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
        indicator.setAttribute("role", "img");
        row.prepend(indicator);
      }
      if (indicator) {
        const base = clippingBase(nodes, node);
        indicator.title = base
          ? `Обтравочная маска: ${base.layer.name || "слой ниже"}`
          : "Обтравочная маска: переместите над слоем";
        indicator.setAttribute("aria-label", indicator.title);
      }
      if (!node.layer.clipToBelow) indicator?.remove();
      row.classList.toggle("is-clipping-base", clippingBases.has(node));
    }
    for (const id of groups) {
      const members = this.members.get(id)!,
        eligible = members.filter(selectable);
      let row = this.rows.get(id);
      if (!row) {
        row = document.createElement("div");
        row.className = "native-layer-row layer-group-item";
        row.dataset.groupId = id;
        row.innerHTML =
          '<button class="native-layer-name" type="button"><span class="layer-icon layer-icon-group" aria-hidden="true"></span><span class="layer-label"></span></button><span class="layer-actions layer-group-actions"><button class="layer-group-toggle" type="button" aria-label="Раскрыть или свернуть группу">▴</button><button data-group-action="visible" type="button"></button><button data-group-action="locked" type="button"></button></span>';
        const r = row;
        for (const flag of ["visible", "locked"] as const) {
          row.querySelector<HTMLButtonElement>(
            `[data-group-action="${flag}"]`,
          )!.onclick = () => {
            if (this.blocked()) return;
            const members = groupMembers(this.session.page.nodes, id);
            const value =
              flag === "visible"
                ? members.every((n) => n.appearance.visible === false)
                : !members.every((n) => n.layer.locked);
            this.session.replaceMany(
              members.map((n) =>
                flag === "visible"
                  ? { ...n, appearance: { ...n.appearance, visible: value } }
                  : { ...n, layer: { ...n.layer, locked: value } },
              ),
            );
            this.session.select([]);
            this.session.finish();
            this.changed();
          };
        }
        row.querySelector<HTMLButtonElement>(".layer-group-toggle")!.onclick =
          () =>
            this.groupPatch(id, {
              groupCollapsed: !groupMembers(this.session.page.nodes, id)[0]
                ?.layer.groupCollapsed,
            });
        row.onclick = (e) => {
          if ((e.target as HTMLElement).closest(".layer-actions,input")) return;
          this.select();
          const ids = groupMembers(this.session.page.nodes, id)
            .filter(selectable)
            .map((n) => n.id);
          this.session.select(
            e.ctrlKey || e.metaKey
              ? ids.every((k) => this.session.selectedIds.includes(k))
                ? this.session.selectedIds.filter((k) => !ids.includes(k))
                : [...this.session.selectedIds, ...ids]
              : ids,
          );
          this.changed();
        };
        row.ondblclick = (e) => {
          if ((e.target as HTMLElement).closest(".layer-actions,input")) return;
          e.preventDefault();
          this.rename(id, r);
        };
        // Keep the group's existing edit/modal guard; NativeLayerDrag owns the gesture.
        row.onpointerdown = (e) => {
          if (this.blocked() || this.renaming) e.stopPropagation();
        };
        this.rows.set(id, row);
      }
      const block = blockRoots(members).length > 0;
      row.dataset.block = String(block);
      row.querySelector<HTMLElement>(".layer-group-toggle")!.hidden = block;
      const icon = row.querySelector<HTMLElement>(".layer-icon")!;
      icon.classList.toggle("layer-icon-group", !block);
      if (block) setNativeIcon(icon, "block");
      else icon.replaceChildren();
      const count = eligible.filter((n) =>
        this.session.selectedIds.includes(n.id),
      ).length;
      row.dataset.groupSelection =
        count === 0 ? "none" : count === eligible.length ? "all" : "partial";
      row.querySelector<HTMLElement>(".layer-label")!.textContent = String(
        members[0]!.layer.groupName ?? "Группа",
      );
      row.querySelector(".layer-group-toggle")!.textContent = members[0]!.layer
        .groupCollapsed
        ? "▾"
        : "▴";
      const hidden = members.every((n) => n.appearance.visible === false),
        locked = members.every((n) => n.layer.locked);
      for (const [flag, icon, title] of [
        [
          "visible",
          hidden ? "hidden" : "visible",
          hidden ? "Показать группу" : "Скрыть группу",
        ],
        [
          "locked",
          locked ? "locked" : "unlocked",
          locked ? "Разблокировать группу" : "Заблокировать группу",
        ],
      ]) {
        const button = row.querySelector<HTMLButtonElement>(
          `[data-group-action="${flag}"]`,
        )!;
        setNativeIcon(button, icon!);
        button.title = button.ariaLabel = title!;
      }
      const ordered = members.slice().reverse(),
        first = this.list.querySelector(`[data-layer-id="${ordered[0]!.id}"]`);
      row.dataset.groupFirstLayerId = ordered[0]!.id;
      if (first) {
        this.list.insertBefore(row, first);
      }
    }
  }
}
